<template>
    <div class="bcp" :class="{ 'bcp--open': isOpen }">
        <!-- Ville retenue : on l'affiche comme une pastille, la saisie libre n'existe plus -->
        <div v-if="value && !editing" class="bcp-selected">
            <span class="bcp-selected-icon"><i class="fal fa-city"></i></span>
            <span class="bcp-selected-text">
                <strong>{{ value }}</strong>
                <small v-if="selectedMeta">{{ selectedMeta }}</small>
            </span>
            <button type="button" class="bcp-action" :title="$t('banoc.city_change')" @click="startEditing">
                <i class="fal fa-pen"></i>
            </button>
            <button v-if="clearable" type="button" class="bcp-action bcp-action--danger" :title="$t('banoc.city_clear')" @click="clear">
                <i class="fal fa-times"></i>
            </button>
        </div>

        <div v-else ref="trigger" class="bcp-input-wrap">
            <i class="fal fa-search bcp-input-icon"></i>
            <input
                ref="input"
                v-model="query"
                type="text"
                class="bcp-input"
                autocomplete="off"
                :id="inputId"
                :placeholder="$t('banoc.city_placeholder')"
                @input="onInput"
                @focus="onFocus"
                @keydown.down.prevent="move(1)"
                @keydown.up.prevent="move(-1)"
                @keydown.enter.prevent="pickActive"
                @keydown.esc="cancel"
            />
            <i v-if="loading" class="fal fa-spinner-third fa-spin bcp-input-spinner"></i>
        </div>

        <portal v-if="isOpen" to="dropdown-portal">
            <div ref="panel" class="bcp-panel" :style="panelStyle">
                <ul v-if="results.length" class="bcp-list">
                    <li
                        v-for="(city, index) in results"
                        :key="city.jurisd_local_id"
                        class="bcp-item"
                        :class="{ 'bcp-item--active': index === activeIndex }"
                        @mousedown.prevent="pick(city)"
                        @mouseenter="activeIndex = index"
                    >
                        <span class="bcp-item-main">
                            <strong>{{ city.city }}</strong>
                            <small>{{ city.country && city.country.name }}</small>
                        </span>
                        <span v-if="city.iso_label" class="bcp-item-code">{{ city.iso_label }}</span>
                    </li>
                </ul>
                <div v-else-if="error" class="bcp-empty bcp-empty--error">
                    <i class="fal fa-plug mr-2"></i>{{ $t('banoc.city_error') }}
                </div>
                <div v-else-if="!loading" class="bcp-empty">
                    <i class="fal fa-map-marker-question mr-2"></i>{{ $t('banoc.city_no_result', { query: query.trim() }) }}
                </div>
            </div>
        </portal>

        <!-- Aide : d'où viennent les villes et pourquoi on ne peut plus les taper librement -->
        <div class="bcp-help" :class="{ 'bcp-help--error': error }">
            <div class="bcp-help-head">
                <span class="bcp-help-dot"></span>
                <strong>{{ error ? $t('banoc.help_title_error') : $t('banoc.help_title') }}</strong>
            </div>
            <p v-if="help">{{ help }}</p>
            <p class="bcp-help-tip">
                <i class="fal fa-lightbulb mr-1"></i>{{ $t('banoc.help_tip') }}
            </p>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { BANOC_API_URL } from '@/config/geolocation'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 300

/*
 * Choix d'une ville dans le référentiel BANOC (création d'une compagnie,
 * restriction de ville d'un champ carte BANOC) : la ville se choisit au lieu
 * d'être tapée, pour que le nom stocké soit exactement celui que BANOC renvoie. L'API est publique — on l'appelle en
 * `fetch` nu pour ne pas lui transmettre le jeton du backoffice.
 */
export default {
    name: 'BanocCityPicker',
    props: {
        value: { type: String, default: '' },
        // Pays / code ISO de la ville retenue, pour le sous-titre de la pastille.
        country: { type: String, default: '' },
        isoLabel: { type: String, default: '' },
        // Texte de l'encart d'aide propre à l'écran qui utilise le sélecteur.
        help: { type: String, default: '' },
        clearable: { type: Boolean, default: true },
        inputId: { type: String, default: null }
    },
    data() {
        return {
            query: '',
            results: [],
            loading: false,
            error: false,
            isOpen: false,
            editing: false,
            activeIndex: -1,
            panelStyle: {},
            searchTimeout: null,
            controller: null
        }
    },
    computed: {
        selectedMeta() {
            return [this.country, this.isoLabel].filter(Boolean).join(' · ')
        }
    },
    watch: {
        isOpen(open) {
            if (open) {
                this.$nextTick(this.updatePosition)
                document.addEventListener('mousedown', this.onOutsideClick)
                window.addEventListener('scroll', this.updatePosition, true)
                window.addEventListener('resize', this.updatePosition)
            } else {
                this.unbind()
            }
        }
    },
    beforeDestroy() {
        clearTimeout(this.searchTimeout)
        if (this.controller) this.controller.abort()
        this.unbind()
    },
    methods: {
        unbind() {
            document.removeEventListener('mousedown', this.onOutsideClick)
            window.removeEventListener('scroll', this.updatePosition, true)
            window.removeEventListener('resize', this.updatePosition)
        },
        startEditing() {
            this.editing = true
            this.query = ''
            this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
        },
        onFocus() {
            if (this.query.trim().length >= MIN_QUERY_LENGTH) this.isOpen = true
        },
        onInput() {
            clearTimeout(this.searchTimeout)
            const q = this.query.trim()
            if (q.length < MIN_QUERY_LENGTH) {
                this.results = []
                this.isOpen = false
                return
            }
            this.searchTimeout = setTimeout(() => this.search(q), DEBOUNCE_MS)
        },
        async search(q) {
            // Une frappe rapide ne doit pas laisser une réponse périmée écraser la bonne.
            if (this.controller) this.controller.abort()
            this.controller = new AbortController()
            this.loading = true
            this.isOpen = true
            try {
                const params = new URLSearchParams({ q, format: 'city' })
                const res = await fetch(`${BANOC_API_URL}/api/geocode/all?${params}`, {
                    headers: { Accept: 'application/json' },
                    signal: this.controller.signal
                })
                if (!res.ok) throw new Error(`BANOC ${res.status}`)
                const data = await res.json()
                this.results = (data && data.cities) || []
                this.activeIndex = this.results.length ? 0 : -1
                this.error = false
            } catch (e) {
                if (e.name === 'AbortError') return
                this.results = []
                this.error = true
            } finally {
                this.loading = false
                this.$nextTick(this.updatePosition)
            }
        },
        move(step) {
            if (!this.isOpen || !this.results.length) return
            const count = this.results.length
            this.activeIndex = (this.activeIndex + step + count) % count
        },
        pickActive() {
            const city = this.results[this.activeIndex]
            if (city) this.pick(city)
        },
        pick(city) {
            this.$emit('input', city.city)
            this.$emit('select', city)
            this.reset()
        },
        clear() {
            this.$emit('input', '')
            this.$emit('select', null)
            this.reset()
        },
        // Échap ou clic ailleurs : on revient à la ville déjà retenue, rien de tapé n'est gardé.
        cancel() {
            this.reset()
        },
        reset() {
            clearTimeout(this.searchTimeout)
            this.query = ''
            this.results = []
            this.isOpen = false
            this.editing = false
            this.activeIndex = -1
        },
        onOutsideClick(e) {
            const insideTrigger = this.$el && this.$el.contains(e.target)
            const insidePanel = this.$refs.panel && this.$refs.panel.contains(e.target)
            if (!insideTrigger && !insidePanel) this.cancel()
        },
        updatePosition() {
            if (!this.isOpen || !this.$refs.trigger) return
            const rect = this.$refs.trigger.getBoundingClientRect()
            const vh = window.innerHeight
            const gap = 6
            const panelHeight = (this.$refs.panel && this.$refs.panel.offsetHeight) || 280
            const spaceBelow = vh - rect.bottom
            const flip = spaceBelow < panelHeight + gap && rect.top > spaceBelow
            const style = {
                position: 'fixed',
                left: rect.left + 'px',
                width: rect.width + 'px',
                zIndex: 10000
            }
            if (flip) {
                style.bottom = (vh - rect.top + gap) + 'px'
            } else {
                style.top = (rect.bottom + gap) + 'px'
            }
            this.panelStyle = style
        }
    }
}
</script>

<style scoped>
.bcp-input-wrap {
    position: relative;
}

.bcp-input {
    width: 100%;
    padding: 9px 34px 9px 34px;
    border: 1.5px solid #e2e8f0;
    border-radius: 9px;
    font-size: 0.84rem;
    color: #0f172a;
    outline: none;
    box-sizing: border-box;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.bcp-input::placeholder {
    color: #cbd5e1;
}

.bcp-input:hover {
    border-color: #cbd5e1;
}

.bcp-input:focus {
    border-color: #0891b2;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.bcp-input-icon,
.bcp-input-spinner {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.82rem;
    color: #94a3b8;
    pointer-events: none;
}

.bcp-input-icon {
    left: 12px;
}

.bcp-input-spinner {
    right: 12px;
    color: #0891b2;
}

.bcp-selected {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 8px 8px 10px;
    border: 1.5px solid #a5f3fc;
    border-radius: 9px;
    background: #ecfeff;
    min-width: 0;
}

.bcp-selected-icon {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #0891b2;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
}

.bcp-selected-icon i {
    color: #fff;
}

.bcp-selected-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}

.bcp-selected-text strong {
    font-size: 0.84rem;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bcp-selected-text small {
    font-size: 0.72rem;
    color: #0e7490;
}

.bcp-action {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 7px;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
}

.bcp-action:hover {
    background: #cffafe;
    color: #0e7490;
}

.bcp-action--danger:hover {
    background: #fee2e2;
    color: #dc2626;
}

.bcp-panel {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
    overflow: hidden;
}

.bcp-list {
    list-style: none;
    margin: 0;
    padding: 4px;
    max-height: 260px;
    overflow-y: auto;
}

.bcp-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 7px;
    cursor: pointer;
}

.bcp-item--active {
    background: #ecfeff;
}

.bcp-item-main {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.25;
}

.bcp-item-main strong {
    font-size: 0.82rem;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bcp-item-main small {
    font-size: 0.7rem;
    color: #64748b;
}

.bcp-item-code {
    flex-shrink: 0;
    font-size: 0.66rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    color: #0e7490;
    background: #cffafe;
    border-radius: 5px;
    padding: 2px 6px;
}

.bcp-empty {
    padding: 12px;
    font-size: 0.78rem;
    color: #64748b;
}

.bcp-empty--error {
    color: #b45309;
}

.bcp-help {
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: 9px;
    background: #f0fdfa;
    border: 1px solid #ccfbf1;
    font-size: 0.72rem;
    color: #475569;
    line-height: 1.45;
}

.bcp-help p {
    margin: 4px 0 0;
}

.bcp-help-head {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #0f766e;
    font-size: 0.74rem;
}

.bcp-help-dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
}

.bcp-help-tip {
    color: #64748b;
}

.bcp-help--error {
    background: #fffbeb;
    border-color: #fde68a;
}

.bcp-help--error .bcp-help-head {
    color: #b45309;
}

.bcp-help--error .bcp-help-dot {
    background: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.18);
}

@media (max-width: 640px) {
    .bcp-input {
        font-size: 16px; /* évite le zoom auto d'iOS au focus */
    }

    .bcp-item {
        padding: 10px;
    }

    .bcp-action {
        width: 34px;
        height: 34px;
    }
}
</style>
